import { Area, Point, RectLayout } from "./GeometricLayout";
import isBetweenInclusive from "./util/isBetweenInclusive";

interface Params {
  /// Usually the window.innerWidth and window.innerHeight (viewport Area)
  boundaryArea?: Area;

  /// The rectangle that the boat should be laid out relative to
  anchor: RectLayout;

  /// The boat that is trying to lay out bottom and left-aligned, relative to the anchor
  boat?: Area;
}
/**
 * Used to lay out the inline card relative to a highlight.
 *
 * "Boat" is a metaphor for an "object" that is anchored to some "anchor", within a constrained area.
 *
 * The boat is biased towards the bottom left-aligned, but if the boundary area doesn't allow it to lay out that way,
 * the boat will then float to the top, or right-aligned, or however it can fit within the boundary area.
 *
 * E.g.
 *
 * +--------------------+
 * |  Constrained area  |
 * |                    |
 * |  +--------+        |
 * |  | Anchor |        |
 * |  +--------+        |
 * |  +-----------+     |
 * |  | Boat      |     |
 * |  +-----------+     |
 * |                    |
 * +--------------------+
 *
 * @returns the point to lay out the boat with a bias towards the bottom left-aligned.
 * If the boundary would intersect that bottom left-aligned layout, then it switches
 * to top and/or right-aligned layout accordingly.
 */
export default function getBoatLayout({
  boundaryArea,
  anchor,
  boat,
}: Params): Point | null {
  if (!boundaryArea || !anchor || !boat) {
    return null;
  }

  // needs to be up or down? left or right?
  // check if you can lay out normally
  const anchorIsWithinBoundary =
    isBetweenInclusive(anchor.top, 0, boundaryArea.height) &&
    isBetweenInclusive(anchor.left, 0, boundaryArea.width);
  if (!anchorIsWithinBoundary) {
    return null;
  }

  // Get the top
  let boatTop = 0;
  const bottommostPoint = anchor.top + anchor.height + boat.height;
  if (bottommostPoint < boundaryArea.height) {
    // we have room to lay out below the anchor, which is our bias
    boatTop = anchor.top + anchor.height;
  } else {
    // lay out above the anchor
    boatTop = anchor.top - boat.height;
  }

  // Get the left
  let boatLeft = 0;
  const rightmostPoint = anchor.left + anchor.width + boat.width;
  if (rightmostPoint < boundaryArea.width) {
    // we can lay out left-aligned
    boatLeft = anchor.left;
  } else {
    // we need to lay out right-aligned
    boatLeft = anchor.left - (boat.width - anchor.width);
  }
  const ret = {
    top: boatTop,
    left: boatLeft,
  };
  return ret;
}
